// Professional MUI Drawer sidebar. Role-based menu logic + active-link
// resolution are preserved verbatim from the original nav.jsx; the only change
// is presentation (MUI List with icons, themed active state, collapsible
// groups, mini/collapsed mode, responsive permanent/temporary drawer).

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";

import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import ContactsOutlinedIcon from "@mui/icons-material/ContactsOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import CheckroomOutlinedIcon from "@mui/icons-material/CheckroomOutlined";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import SystemUpdateAltOutlinedIcon from "@mui/icons-material/SystemUpdateAltOutlined";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";

import { ReactComponent as JKLogo } from "_assets/images/jk.svg";
import { DASHBOARD_PAGES, SUBADMIN_ROLE, grantedResources, pageResource } from "config/dashboardPages";

export const DRAWER_WIDTH = 264;
export const MINI_WIDTH = 76;

// --- Behavior preserved verbatim from nav.jsx ---------------------------------
const pathMatchesLink = (pathname, link) =>
  link === "/" ? pathname === "/" : pathname === link || pathname.startsWith(link + "/");

const getActiveLink = (items, pathname) => {
  const links = items.flatMap((it) =>
    it.children ? it.children.map((c) => c.link) : it.link ? [it.link] : []
  );
  let best = null;
  links.forEach((link) => {
    if (link && pathMatchesLink(pathname, link) && (!best || link.length > best.length)) {
      best = link;
    }
  });
  return best;
};

// Name -> icon lookup (keeps the menu arrays clean and behavior identical).
const ICONS = {
  Dashboard: DashboardOutlinedIcon,
  Home: HomeOutlinedIcon,
  "Visit Stats": BarChartOutlinedIcon,
  "Task Stats": AssignmentTurnedInOutlinedIcon,
  "Dealer Location": LocationOnOutlinedIcon,
  "Dealer Map": MapOutlinedIcon,
  Holiday: EventOutlinedIcon,
  Attendance: FactCheckOutlinedIcon,
  "Dealer Churn": TrendingDownOutlinedIcon,
  Ticketing: SupportAgentOutlinedIcon,
  "Actyv AI": SmartToyOutlinedIcon,
  Reports: SummarizeOutlinedIcon,
  "Dealer Directory": ContactsOutlinedIcon,
  "Switch Beat Requests": SwapHorizOutlinedIcon,
  Sync: SyncOutlinedIcon,
  Onboard: PersonAddAltOutlinedIcon,
  Logout: LogoutOutlinedIcon,
  "Market Research": TravelExploreOutlinedIcon,
  "Wear Nature": CheckroomOutlinedIcon,
  Survey: PollOutlinedIcon,
  "Survey Reconciliation": FactCheckOutlinedIcon,
  "Compliance Inspector": VerifiedUserOutlinedIcon,
  "Upload Docs": UploadFileOutlinedIcon,
  SE: StorefrontOutlinedIcon,
  "SE Depot & User": StorefrontOutlinedIcon,
  "SE Reports": SummarizeOutlinedIcon,
  "Depot & User": StorefrontOutlinedIcon,
  "Admin OTR": AdminPanelSettingsOutlinedIcon,
  PJPs: RouteOutlinedIcon,
  "Dealer Visits": StorefrontOutlinedIcon,
  "Secondary Visits": StoreOutlinedIcon,
  Tasks: AssignmentTurnedInOutlinedIcon,
  Leads: PersonSearchOutlinedIcon,
  "Daily Visits": TodayOutlinedIcon,
  "Time Spent (App)": AccessTimeOutlinedIcon,
  "App Settings": SettingsOutlinedIcon,
  "App Maintenance": BuildCircleOutlinedIcon,
  "Force Update": SystemUpdateAltOutlinedIcon,
  Analytics: AnalyticsOutlinedIcon,
  "Analytics Visits": TimelineOutlinedIcon,
  "Analytics Daily Calls": CallOutlinedIcon,
};

const iconFor = (name) => ICONS[name] || RadioButtonUncheckedIcon;

// A single leaf menu item.
const NavLeaf = ({ name, active, collapsed, onClick }) => {
  const Icon = iconFor(name);
  const button = (
    <ListItemButton
      selected={active}
      onClick={onClick}
      sx={{ px: collapsed ? 1.5 : 2, justifyContent: collapsed ? "center" : "flex-start" }}
    >
      <ListItemIcon sx={{ minWidth: 0, mr: collapsed ? 0 : 2, justifyContent: "center" }}>
        <Icon fontSize="small" />
      </ListItemIcon>
      {!collapsed && <ListItemText primary={name} primaryTypographyProps={{ fontSize: 14 }} />}
    </ListItemButton>
  );
  return collapsed ? (
    <Tooltip title={name} placement="right">
      {button}
    </Tooltip>
  ) : (
    button
  );
};

// A collapsible parent group (e.g. "SE").
const NavGroup = ({ name, children, activeLink, collapsed, navigateTo }) => {
  const isActive = children.some((c) => c.link === activeLink);
  const [open, setOpen] = useState(isActive);
  useEffect(() => {
    if (isActive) setOpen(true);
  }, [isActive]);

  const Icon = iconFor(name);

  if (collapsed) {
    // In mini mode, render children as flat leaves (no room for a group header).
    return children.map((child, i) => (
      <NavLeaf
        key={"grp-mini-" + i}
        name={child.name}
        active={activeLink === child.link}
        collapsed
        onClick={child.execute || (() => navigateTo(child.link))}
      />
    ));
  }

  return (
    <>
      <ListItemButton onClick={() => setOpen((v) => !v)} sx={{ px: 2 }}>
        <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
          <Icon fontSize="small" color={isActive ? "primary" : "inherit"} />
        </ListItemIcon>
        <ListItemText primary={name} primaryTypographyProps={{ fontSize: 14 }} />
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ pl: 2 }}>
          {children.map((child, i) => (
            <NavLeaf
              key={"grp-" + i}
              name={child.name}
              active={activeLink === child.link}
              onClick={child.execute || (() => navigateTo(child.link))}
            />
          ))}
        </List>
      </Collapse>
    </>
  );
};

// Each page renders its own AppShell, so the sidebar remounts on every route
// change and its scrollable menu would otherwise snap back to the top. We
// persist the scroll offset in sessionStorage and restore it on mount so the
// menu stays put as the user navigates between pages.
const SCROLL_KEY = "ui-sidebar-scroll";

const SidebarContent = ({ collapsed }) => {
  const location = useLocation();
  const history = useHistory();
  const { SAP_ID } = useParams();
  const { user } = useSelector((state) => state.user);
  const role = user?.role;

  const listRef = useRef(null);

  // Restore the saved scroll position synchronously before paint to avoid a
  // visible jump on navigation.
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    try {
      const saved = window.sessionStorage.getItem(SCROLL_KEY);
      if (saved != null) el.scrollTop = parseInt(saved, 10) || 0;
    } catch (e) {
      /* ignore */
    }
  }, []);

  const handleScroll = (e) => {
    try {
      window.sessionStorage.setItem(SCROLL_KEY, String(e.currentTarget.scrollTop));
    } catch (err) {
      /* ignore */
    }
  };

  const logoutUser = () => {
    localStorage.setItem("token", "");
    history.push("/");
    window.location.reload();
  };

  // --- Role-based menus (preserved verbatim from nav.jsx) --------------------
  const menus = useMemo(() => {
    const AONavbar = [
      { name: "Dashboard", link: "/dashboard" },
      { name: "Home", link: "/" },
      { name: "Visit Stats", link: "/visit-stats" },
      { name: "Task Stats", link: "/task-stats" },
      { name: "Dealer Location", link: "/update-location" },
      { name: "Holiday", link: "/holiday" },
      { name: "Attendance", link: "/attendance-data" },
      { name: "Dealer Churn", link: "/dealer-churn" },
      { name: "Ticketing", link: "/dealer-help-desk" },
      { name: "Actyv AI", link: "/actyv-ai" },
      { name: "Reports", link: "/vists-download" },
      { name: "Dealer Directory", link: "/dealer-directory" },
      { name: "Dealer Map", link: "/dealer-map" },
      { name: "Switch Beat Requests", link: "/switch-beat" },
      // { name: "Sync", link: "/sync" },
      { name: "Onboard", link: "/onboard" },
      { name: "Logout", execute: logoutUser },
    ];
    const ADMINNavbar = [
      { name: "Dashboard", link: "/dashboard" },
      { name: "Home", link: "/" },
      { name: "Visit Stats", link: "/visit-stats" },
      { name: "Task Stats", link: "/task-stats" },
      { name: "Dealer Location", link: "/update-location" },
      { name: "Dealer Churn", link: "/dealer-churn" },
      { name: "Market Research", link: "/market-research" },
      { name: "Actyv AI", link: "/actyv-ai" },
      { name: "Ticketing", link: "/dealer-help-desk" },
      { name: "Wear Nature", link: "/wear-nature" },
      { name: "Survey", link: "/survey" },
      { name: "Survey Reconciliation", link: "/survey-reconciliation" },
      { name: "Compliance Inspector", link: "/compliance-inspect" },
      { name: "Upload Docs", link: "/upload-documents" },
      { name: "Holiday", link: "/holiday" },
      { name: "Attendance", link: "/attendance-data" },
      { name: "Reports", link: "/vists-download" },
      { name: "Dealer Directory", link: "/dealer-directory" },
      { name: "Dealer Map", link: "/dealer-map" },
      {
        name: "SE",
        children: [
          { name: "Depot & User", link: "/se-depot" },
          { name: "Reports", link: "/se-reports" },
          { name: "Analytics", link: "/se-analytics" },
          { name: "Analytics Visits", link: "/se-analytics/visits" },
          { name: "Analytics Daily Calls", link: "/se-analytics/daily-calls" },
        ],
      },
      {
        name: "App Settings",
        children: [
          { name: "App Maintenance", link: "/app-maintenance" },
          { name: "Force Update", link: "/force-update" },
        ],
      },
      // { name: "Sync", link: "/sync" },
      { name: "Onboard", link: "/onboard" },
      { name: "Roles & Permissions", link: "/roles" },
      { name: "Logout", execute: logoutUser },
    ];
    const AllNav = [
      { name: "Dashboard", link: "/dashboard" },
      { name: "Home", link: "/" },
      { name: "Visit Stats", link: "/visit-stats" },
      { name: "Task Stats", link: "/task-stats" },
      { name: "Dealer Location", link: "/update-location" },
      { name: "Dealer Churn", link: "/dealer-churn" },
      { name: "Holiday", link: "/holiday" },
      { name: "Attendance", link: "/attendance-data" },
      { name: "Actyv AI", link: "/actyv-ai" },
      { name: "Reports", link: "/vists-download" },
      { name: "Ticketing", link: "/dealer-help-desk" },
      { name: "Dealer Directory", link: "/dealer-directory" },
      // { name: "Sync", link: "/sync" },
      { name: "Onboard", link: "/onboard" },
      { name: "Logout", execute: logoutUser },
    ];
    const ZTMNavbar = [
      { name: "Reports", link: "/vists-download" },
      { name: "Logout", execute: logoutUser },
    ];
    const ADMINOTRNavbar = [
      { name: "Admin OTR", link: "/admin-otr" },
      { name: "Logout", execute: logoutUser },
    ];
    const SENavbar = [
      { name: "SE Depot & User", link: "/se-depot" },
      { name: "SE Reports", link: "/se-reports" },
      { name: "Analytics", link: "/se-analytics" },
      { name: "Analytics Visits", link: "/se-analytics/visits" },
      { name: "Analytics Daily Calls", link: "/se-analytics/daily-calls" },
      // Roles & Permissions is ADMINSE-only (plain SE users cannot create roles).
      ...(role === "ADMINSE" ? [{ name: "Roles & Permissions", link: "/se-roles" }] : []),
      { name: "Logout", execute: logoutUser },
    ];
    const navItems1 = [
      { name: "Home", link: "/" },
      { name: "PJPs", link: `/SO/${SAP_ID}/PJP` },
      { name: "Dealer Visits", link: `/SO/${SAP_ID}/visits` },
      { name: "Secondary Visits", link: `/SO/${SAP_ID}/prospectus/visits` },
      { name: "Tasks", link: `/SO/${SAP_ID}/tasks` },
      { name: "Attendance", link: `/SO/${SAP_ID}/attendance` },
      { name: "Daily Visits", link: `/SO/${SAP_ID}/daily` },
      { name: "Leads", link: `/SO/${SAP_ID}/leads` },
      { name: "Time Spent (App)", link: `/SO/${SAP_ID}/daily/timespent` },
      { name: "Logout", execute: logoutUser },
    ];

    // Sub-admin menu: only the pages granted in their permission matrix.
    const granted = grantedResources(user?.permissions);
    const SUBADMINNavbar = [
      ...DASHBOARD_PAGES.filter((pg) => granted.has(pageResource(pg))).map((pg) => ({
        name: pg.label,
        link: pg.link,
      })),
      { name: "Logout", execute: logoutUser },
    ];

    let main;
    if (role === "ADMIN") main = ADMINNavbar;
    else if (role === SUBADMIN_ROLE) main = SUBADMINNavbar;
    else if (role === "AO" || role === "RO") main = AONavbar;
    else if (role === "ZTM") main = ZTMNavbar;
    else if (role === "ADMINOTR") main = ADMINOTRNavbar;
    else if (role === "SE" || role === "ADMINSE") main = SENavbar;
    else main = AllNav;

    return { main, navItems1 };
  }, [role, SAP_ID, user]);

  const mainMenuPaths = [
    "/", "/onboard", "/sync", "/update-location", "/switch-beat",
    "/upload-documents", "/add-documents", "/vists-download", "/dashboard",
    "/visit-stats", "/task-stats", "/wear-nature", "/dealer-churn", "/actyv-ai",
    "/dealer-help-desk", "/holiday", "/dealer-directory", "/dealer-map", "/reimbursement",
    "/reimbursement-category", "/attendance-data", "/expense-dash",
    "/spend-limit", "/survey", "/survey-reconciliation", "/market-research",
    "/se-depot", "/se-reports", "/se-analytics", "/se-analytics/visits",
    "/se-analytics/daily-calls", "/admin-otr", "/documentation",
    "/compliance-inspect", "/app-maintenance", "/force-update", "/roles",
    "/se-roles",
  ];
  const showMainMenu = mainMenuPaths.some((p) => pathMatchesLink(location.pathname, p));
  // A sub-admin granted Home ("/") joins the Home → officer drill-down flow
  // (navItems1 — PJPs, Dealer Visits, Tasks, …) the same way an admin does when
  // they click into an officer. Every other sub-admin only ever sees their
  // granted-pages menu.
  const subAdminInHomeFlow =
    role === SUBADMIN_ROLE && grantedResources(user?.permissions).has("/");
  const currentMenu =
    role === SUBADMIN_ROLE && !subAdminInHomeFlow
      ? menus.main
      : showMainMenu
      ? menus.main
      : menus.navItems1;
  const activeLink = getActiveLink(currentMenu, location.pathname);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Logo header */}
      <Box
        onClick={() => history.push("/")}
        sx={{
          height: 90,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
          "& svg": { height: collapsed ? 40 : 64, maxWidth: "100%" },
        }}
      >
        <JKLogo />
      </Box>

      {/* Scrollable menu */}
      <List
        ref={listRef}
        onScroll={handleScroll}
        sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 1, minHeight: 0 }}
      >
        {currentMenu.map((item, index) =>
          item.children ? (
            <NavGroup
              key={"grp-" + index}
              name={item.name}
              children={item.children}
              activeLink={activeLink}
              collapsed={collapsed}
              navigateTo={(link) => history.push(link)}
            />
          ) : (
            <NavLeaf
              key={"leaf-" + index}
              name={item.name}
              active={item.link === activeLink}
              collapsed={collapsed}
              onClick={item.execute || (() => history.push(item.link))}
            />
          )
        )}
      </List>

    </Box>
  );
};

/**
 * Responsive sidebar. Permanent (with mini/collapsed mode) on desktop,
 * temporary drawer on mobile.
 */
const Sidebar = ({ collapsed = false, mobileOpen = false, onMobileClose }) => {
  const width = collapsed ? MINI_WIDTH : DRAWER_WIDTH;
  return (
    <Box
      component="nav"
      sx={{ width: { md: width }, flexShrink: { md: 0 } }}
      aria-label="main navigation"
    >
      {/* Mobile: temporary */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
        }}
      >
        <SidebarContent collapsed={false} />
      </Drawer>

      {/* Desktop: permanent */}
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width,
            boxSizing: "border-box",
            overflowX: "hidden",
            transition: (t) =>
              t.transitions.create("width", {
                easing: t.transitions.easing.sharp,
                duration: t.transitions.duration.enteringScreen,
              }),
          },
        }}
      >
        <SidebarContent collapsed={collapsed} />
      </Drawer>
    </Box>
  );
};

export default Sidebar;
